// Data transformation utilities for analytics charts
import { commoditySizeRules } from '../components/charts/pulse/index.js';

/**
 * Get dates between two dates
 */
const getDatesBetween = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate.getTime());

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

/**
 * Calculate ISO week number for a given date
 */
export const getISOWeekNumber = (date) => {
  const target = new Date(date.valueOf());
  const dayNum = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNum + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 1);
  if (firstThursday.getDay() !== 4) {
    firstThursday.setMonth(0, 1 + ((4 - firstThursday.getDay() + 7) % 7));
  }
  return 1 + Math.round((target - firstThursday) / (7 * 24 * 60 * 60 * 1000));
};

/**
 * Add missing dates in graph data
 */
const addMissingDatesInGraphData = (data) => {
  if (!data || data.length === 0) return data;

  const dates = getDatesBetween(
    new Date(data[0].date_string),
    new Date(data[data.length - 1].date_string)
  );

  const newData = [];
  let i = 0;
  let j = 0;

  while (i < dates.length && j < data.length) {
    const dateMatch = dates[i].toDateString() === new Date(data[j].date_string).toDateString();
    if (dateMatch) {
      newData.push(data[j]);
      i++;
      j++;
    } else {
      // Add missing date with year and iso_week_number for proper filtering
      const missingDate = dates[i];
      newData.push({
        date_string: missingDate,
        year: missingDate.getFullYear(),
        iso_week_number: getISOWeekNumber(missingDate),
      });
      i++;
    }
  }

  // Add remaining dates
  while (i < dates.length) {
    const missingDate = dates[i];
    newData.push({
      date_string: missingDate,
      year: missingDate.getFullYear(),
      iso_week_number: getISOWeekNumber(missingDate),
    });
    i++;
  }

  return newData;
};

/**
 * Add missing keys in graph data (ensure all items have same keys)
 */
const addMissingKeysInGraphData = (data) => {
  if (!data || data.length === 0) return data;
  
  const allKeys = new Set();
  for (const item of data) {
    Object.keys(item).forEach(key => allKeys.add(key));
  }
  
  return data.map(item => {
    const newItem = {};
    for (const key of allKeys) {
      newItem[key] = item[key] !== undefined ? item[key] : 0;
    }
    return newItem;
  });
};

/**
 * Format date for display (e.g., "6 Jun 2025")
 */
export const formatDate = (date) => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Format date short (e.g., "6 Jun")
 */
export const formatDateShort = (date) => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  return `${date.getDate()} ${months[date.getMonth()]}`;
};

/**
 * Parse date from DD/MM/YYYY, ISO strings, or Date instances (shared by price + volume prep).
 */
export const getDateObject = (dateString) => {
  if (!dateString) return null;
  if (dateString instanceof Date) {
    return !isNaN(dateString.getTime()) ? dateString : null;
  }
  const str = String(dateString).trim();
  if (!str) return null;
  const parts = str.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    if (!day || !month || !year) return null;
    const parsed = new Date(year, month - 1, day);
    return !isNaN(parsed.getTime()) ? parsed : null;
  }
  const iso = new Date(str);
  return !isNaN(iso.getTime()) ? iso : null;
};

/**
 * Transform date strings to Date objects and sort
 */
const transformDateStringsToDateObjectsAndSort = (data) => {
  const withDateObj = data.map((item) => ({
    ...item,
    date_string: getDateObject(item.date_string),
  }));
  const validOnly = withDateObj.filter(
    (item) => item.date_string != null && !isNaN(item.date_string.getTime())
  );
  validOnly.sort((a, b) => a.date_string.getTime() - b.date_string.getTime());
  return validOnly;
};

/**
 * Merge historic data with same date
 */
const mergeHistoricDataWithSameDate = (sortedData) => {
  const mergedData = [];
  for (const item of sortedData) {
    const lastAddedItem = mergedData[mergedData.length - 1];

    if (
      lastAddedItem &&
      lastAddedItem.date_string.toDateString() === item.date_string.toDateString()
    ) {
      lastAddedItem[item.district] = item.vol;
      // Season-overlay rows can share a display date but belong to different ISO weeks
      // (current-year real week vs. a prior year's original week). Record each district's
      // own week so weekly aggregation buckets it correctly instead of using one shared week.
      lastAddedItem.district_weeks[item.district] = item.iso_week_number;
    } else {
      const newItem = {
        date_string: item.date_string,
        year: item.year,
        iso_week_number: item.iso_week_number,
        type: item.type,
        [item.district]: item.vol,
        district_weeks: { [item.district]: item.iso_week_number },
      };
      mergedData.push(newItem);
    }
  }
  return mergedData;
};

/**
 * Prepare prices data for chart
 */
export const preparePricesData = (fetchedTableData) => {
  if (!fetchedTableData || fetchedTableData.length === 0) return [];

  const withDateObj = fetchedTableData.map((item) => ({
    ...item,
    date_string: getDateObject(item.date_string),
  }));

  const validOnly = withDateObj.filter(
    (item) => item.date_string != null && !isNaN(item.date_string.getTime())
  );

  validOnly.sort((a, b) => a.date_string.getTime() - b.date_string.getTime());

  return validOnly.map((item) => ({
    ...item,
    date_string: formatDateShort(item.date_string),
  }));
};

/**
 * Prepare stacked bar data for volume chart
 */
export const prepareStackedBarData = (fetchedBarData) => {
  if (!fetchedBarData || fetchedBarData.length === 0) {
    return [];
  }

  const transformedData = fetchedBarData
    .map((item) => {
      const isoWeek = item.iso_week != null ? Number(item.iso_week) : (item.iso_week_number != null ? Number(item.iso_week_number) : undefined);
      const weeklyVol = item.weekly_vol != null ? parseInt(item.weekly_vol, 10) || 0 : undefined;
      const isoYear = item.iso_year != null ? Number(item.iso_year) : undefined;
      const dateObj = getDateObject(item.date_string);
      return {
        ...item,
        date_string: dateObj,
        vol: parseInt(String(item.vol ?? item.volume ?? item.daily_vol ?? 0), 10) || 0,
        iso_week: isoWeek,
        iso_year: isoYear,
        weekly_vol: weeklyVol,
        variety: [null, undefined, "N/A", "Not Classified"].includes(item.variety)
          ? "N/A"
          : item.variety,
      };
    })
    .filter((item) => item.date_string != null && !Number.isNaN(item.date_string.getTime()))
    .sort((a, b) => a.date_string.getTime() - b.date_string.getTime());

  return transformedData;
};

/**
 * Prepare line data for historic volume chart (splits organic/conventional)
 */
export const prepareLineData = (fetchedLineData) => {
  if (!fetchedLineData || fetchedLineData.length === 0) {
    return { organic: [], conventional: [], usaOrigins: [] };
  }

  let organicData = [];
  let conventionalData = [];
  // Origins the backend flags as USA regions (`is_usa`), without the "{year}_" prefix.
  // Empty when the backend does not send the flag; the UI then hides the grouping.
  const usaOrigins = new Set();

  for (const item of fetchedLineData) {
    if (item.is_usa === true && item.district) {
      usaOrigins.add(String(item.district).replace(/^\d{4}_/, ''));
    }
    if (item.type === "Organic") {
      organicData.push(item);
    } else {
      conventionalData.push(item);
    }
  }

  const prepare = (data) => {
    if (data.length === 0) return [];
    
    const transformedData = transformDateStringsToDateObjectsAndSort(data);
    const mergedData = mergeHistoricDataWithSameDate(transformedData);

    if (mergedData.length === 0) return [];

    const withMissingDates = addMissingDatesInGraphData(mergedData);
    const withMissingKeys = addMissingKeysInGraphData(withMissingDates);
    return withMissingKeys;
  };

  return {
    organic: prepare(organicData),
    conventional: prepare(conventionalData),
    usaOrigins: [...usaOrigins],
  };
};

/**
 * Prepare predictions data
 */
export const preparePredictionsData = (data) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => ({
    district: item.district || item.origin || item.region || "",
    season_number: item.season_number || 1,
    expected_start_date: item.expected_start_date || "",
    expected_finish_date: item.expected_finish_date || "",
    expected_volume: item.expected_volume || 0,
    vol_to_date: item.vol_to_date ?? 0,
    expected_season_length: item.expected_season_length || 0,
    season_start: item.season_start || "",
    season_end: item.season_end || null,
    running_days: item.running_days || 0,
    days_to_end: item.days_to_end || 0,
    perc_vol: item.perc_vol || 0,
    perc_season_length: item.perc_season_length || 0,
    type: item.type,
  }));
};

/**
 * Prepare cluster data
 */
export const prepareClusterData = (data) => {
  if (!data || !Array.isArray(data)) return [];

  const toNumberOr = (v, fallback = 0) => {
    if (v === null || v === undefined || v === '') return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const normalizePriceCategory = (raw) => {
    if (raw === null || raw === undefined) return '';
    const s = String(raw).trim();
    if (!s) return '';

    // Normalize capitalization / punctuation variants.
    const key = s.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

    // Already in canonical form (case-insensitive).
    if (key === 'lower than usual') return 'Lower than usual';
    if (key === 'usual') return 'Usual';
    if (key === 'higher than usual') return 'Higher than usual';

    // Common backend / ML labels.
    if (['low', 'lower', 'below', 'lower than avg', 'lower than average', 'lower than normal', 'below average'].includes(key)) {
      return 'Lower than usual';
    }
    if (['balanced', 'normal', 'avg', 'average', 'mid', 'medium', 'neutral', 'typical'].includes(key)) {
      return 'Usual';
    }
    if (['high', 'higher', 'above', 'higher than avg', 'higher than average', 'higher than normal', 'above average'].includes(key)) {
      return 'Higher than usual';
    }

    // If it's something unexpected, pass through trimmed string (better than dropping entirely).
    return s;
  };

  // Backend payloads have varied historically (e.g. iso_week vs iso_week_number).
  // Normalize to the field names expected by `ClusterChart.jsx`.
  return data.map((item) => {
    const isoWeek =
      item?.iso_week_number ??
      item?.iso_week ??
      item?.isoWeek ??
      item?.week ??
      item?.week_number;

    const isoYear =
      item?.iso_year ??
      item?.isoYear ??
      item?.year ??
      item?.iso_year_number;

    const isoWeekStart =
      item?.iso_week_start ??
      item?.week_start ??
      item?.week_start_date ??
      item?.iso_week_start_date ??
      '';

    const isoWeekEnd =
      item?.iso_week_end ??
      item?.week_end ??
      item?.week_end_date ??
      item?.iso_week_end_date ??
      '';

    const avgPrice =
      item?.avg_price ??
      item?.average_price ??
      item?.avgPrice ??
      item?.price_avg ??
      0;

    const priceCategory =
      item?.price_category ??
      item?.priceCategory ??
      item?.category ??
      item?.price_bucket ??
      '';

    return {
      commodity: item?.commodity,
      iso_week_number: toNumberOr(isoWeek, 0),
      iso_week_start: isoWeekStart || '',
      iso_week_end: isoWeekEnd || '',
      grade: item?.grade || '',
      package: item?.package || '',
      variety: item?.variety || '',
      item_size: item?.item_size ?? item?.size ?? '',
      type: item?.type,
      avg_price: toNumberOr(avgPrice, 0),
      cluster_id: item?.cluster_id ?? item?.clusterId ?? item?.cluster,
      price_category: normalizePriceCategory(priceCategory),
      iso_year: toNumberOr(isoYear, 0),
      environment: item?.environment,
      properties: item?.properties,
    };
  });
};

/**
 * Prepare price classifier data (passthrough — data used as-is for filter computation)
 */
export const preparePriceClassifierData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  return data;
};

/**
 * Prepare producing regions data for map display
 * Data comes pre-processed from backend with harvest_status calculated
 */
export const prepareProducingRegionsData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  // Data comes pre-processed from backend, just pass through
  return data;
};

/**
 * Round up to significant figures
 */
export const roundUpto = (n_float, upto) => {
  const n = Math.round(n_float);
  const len = String(n).length;
  if (len < upto) return n;
  
  const roundedBeginning = Number(String(n).slice(0, upto)) + 1;
  const roundedEnd = Math.pow(10, len - upto);
  return roundedBeginning * roundedEnd;
};

/**
 * Calculate Y-axis tick values
 */
export const getYTicks = (data, yKeys) => {
  let maxYValue = 0;
  for (const item of data) {
    for (const key of yKeys) {
      if (item[key] > maxYValue) {
        maxYValue = item[key];
      }
    }
  }

  const step = roundUpto(maxYValue / 6, 1);
  return Array.from({ length: 7 }, (_, i) => i * step);
};

/**
 * Lighten a hex color
 */
export const lightenColor = (color, amount) => {
  // amount: 0 = original, 1 = white
  amount = Math.max(0, Math.min(1, amount));
  color = color.replace("#", "");
  
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);

  r = Math.round(r + (255 - r) * amount);
  g = Math.round(g + (255 - g) * amount);
  b = Math.round(b + (255 - b) * amount);

  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Format large numbers (e.g., 1.5M, 200K)
 */
export const formatLargeNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num.toString();
};

/**
 * Format numbers without abbreviation (no K/M), using locale separators.
 * Keeps small fractional parts if present.
 */
export const formatExactNumber = (value) => {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '';
  const isInt = Number.isInteger(num);
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: isInt ? 0 : 2,
  }).format(num);
};

// ---------------------------------------------------------------------------
// Placeholders in the cascading filters.
// Placeholder values ("N/A", "Not Classified", "None", "null", blank) are hidden
// from the dropdowns and never auto-picked, but ONLY for the commodities below,
// whose data layer is being cleaned so that a hidden value is never the only way
// to reach those rows. Every other commodity keeps the lists exactly as before
// this change: "N/A"/"Not Classified" is offered beside real values and only a
// lone placeholder hides the dimension (see the legacy* functions). Callers that
// pass no commodity (the procurement planner) get that same pre-change list.
// ---------------------------------------------------------------------------

/**
 * Commodities whose filter dropdowns hide placeholder values, spelled as the
 * chart endpoints spell them; matched case-insensitively. Add a commodity here
 * once its classification work is done.
 * Stage 2 adds the four tomato codes: 'tomatoes', 'tomatoes, cherry',
 * 'tomatoes, grape type', 'tomatoes, plum type'.
 */
export const PLACEHOLDER_FREE_COMMODITIES = Object.freeze([
  'cucumbers',
]);

const normalizeCommodity = (commodity) => String(commodity ?? '').trim().toLowerCase();
const PLACEHOLDER_FREE_SET = new Set(PLACEHOLDER_FREE_COMMODITIES.map(normalizeCommodity));

/** True when `commodity` is listed in PLACEHOLDER_FREE_COMMODITIES; blank or unknown -> false. */
export const hidesPlaceholders = (commodity) => PLACEHOLDER_FREE_SET.has(normalizeCommodity(commodity));

/** Placeholder spellings that mean "no value" (vocabulary; commodity-independent). */
export const SENTINEL_OPTION_VALUES = new Set(['n/a', 'na', 'not classified', 'none', 'null', '']);

/** True when `value` is a placeholder in any casing. Whether it is hidden is decided per commodity by the helpers below. */
export const isSentinelOption = (value) => {
  const v = value == null ? '' : String(value).trim().toLowerCase();
  return SENTINEL_OPTION_VALUES.has(v);
};

/** True for the Condition value 'Holdovers' in any casing (hidden only for the gated commodity, Condition column only). */
export const isHoldoversCondition = (value) => value != null && String(value).trim().toLowerCase() === 'holdovers';

/** Pre-change option list (unchanged behaviour for commodities not in the list). */
const legacyCleanOptions = (data, field) => {
  const read = typeof field === 'function' ? field : (d) => d[field];
  const filtersToIgnore = ["N/A", "Not Classified"];
  let options = [...new Set(data.map((d) => read(d) || "").filter((v) => v))].sort();
  if (options.length === 1 && filtersToIgnore.includes(options[0])) {
    options = [];
  }
  return options;
};

/**
 * Unique values of `field` for a filter dropdown. For a placeholder-free
 * commodity no placeholder is offered ([] when only placeholders exist, so the
 * dimension is not shown); for any other commodity this is the pre-change list.
 */
export const getCleanOptions = (data, field, commodity) => {
  const read = typeof field === 'function' ? field : (d) => d[field];
  // Owner 2026-10-21: for the gated commodity, Condition 'Holdovers' is treated like N/A (Condition column only).
  const hide = field === 'condition'
    ? (v) => isSentinelOption(v) || isHoldoversCondition(v)
    : isSentinelOption;
  return hidesPlaceholders(commodity)
    ? [...new Set(data.map((d) => read(d) || "").filter((v) => !hide(v)))].sort()
    : legacyCleanOptions(data, read);
};

/** Unique package options (falls back to sku); placeholders hidden only for a placeholder-free commodity. */
export const getCleanPackageOptions = (rows, commodity) => {
  const values = rows.map((d) => d.package || d.sku || '');
  const kept = hidesPlaceholders(commodity) ? values.filter((v) => !isSentinelOption(v)) : values.filter((v) => v);
  return [...new Set(kept)].sort();
};

/**
 * Multi-select filter for charts whose selection defaults to the option list.
 * Placeholder-free commodity: every option selected ("All") returns the rows
 * untouched so placeholder rows still count toward totals. Otherwise, and for an
 * empty or partial selection, rows are kept by membership as before.
 */
export const applyMultiSelectFilter = (rows, field, selected, allOptions, commodity) => {
  if (!allOptions || allOptions.length === 0) return rows;
  if (hidesPlaceholders(commodity) && allOptions.every((o) => selected.includes(o))) return rows;
  if (selected.length === 0) return [];
  return rows.filter((item) => selected.includes(item[field] || ''));
};

/**
 * True when the rows a chart still plots include rows with no value for `field`
 * and the commodity hides placeholders - a chip must not name a single value
 * over such rows (see singleSelectionLabel's unknownRetained). Always false for
 * other commodities, whose chips behave as before.
 */
export const hasPlaceholderRows = (rows, field, commodity) =>
  hidesPlaceholders(commodity) && rows.some((r) => isSentinelOption(r[field]));

/**
 * Get display value for selected field (converts N/A to "Not Classified")
 */
export const getSelectedFieldValue = (value) => {
  return value === "N/A" ? "Not Classified" : value;
};

/**
 * Generate chart colors based on index
 */
export const getChartColor = (index, total) => {
  const hue = (index * 360) / total;
  return `hsl(${hue}, 90%, 65%)`;
};

/**
 * Generate chart colors based on index
 */
export const getRegionChartColor = (index, total) => {
  const basePalette = [
    '#1f77b4', '#d62728', '#2ca02c', '#9467bd', '#ff7f0e',
    '#17becf', '#e377c2', '#8c564b', '#bcbd22', '#7f7f7f',
    '#1a55FF', '#FF1493', '#00A651', '#6A3D9A', '#FF8C00',
    '#008B8B', '#C71585', '#A0522D', '#B8860B', '#4B0082',
  ];

  if (index < basePalette.length) return basePalette[index];
  if (!total || total <= 0) return basePalette[index % basePalette.length];

  const permutedIndex = (index * 23) % total;
  const hue = Math.round((permutedIndex * 360) / total);

  const saturationBands = [88, 78, 92, 82];
  const lightnessBands = [52, 62, 46, 68];
  const band = Math.floor(index / total) % saturationBands.length;
  const saturation = saturationBands[band];
  const lightness = lightnessBands[band];

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Default chart colors
 */
export const CHART_COLORS = [
  "#4CAF50", // Green
  "#2196F3", // Blue
  "#FF9800", // Orange
  "#9C27B0", // Purple
  "#F44336", // Red
  "#00BCD4", // Cyan
  "#E91E63", // Pink
  "#8BC34A", // Light Green
  "#FF5722", // Deep Orange
  "#607D8B", // Blue Grey
  "#1565C0",
  "#6A1B9A",
  "#00838F",
  "#EF6C00",
  "#C62828",
  "#2E7D32",
  "#AD1457",
  "#4527A0",
  "#00695C",
  "#4E342E",
];

/**
 * Get current ISO week number (ISO 8601 compliant)
 * ISO week 1 is the week containing the first Thursday of the year
 */
export const getCurrentISOWeek = () => {
  const now = new Date();
  const target = new Date(now.valueOf());
  // ISO week starts on Monday, so adjust day number (Mon=1, Sun=7)
  const dayNum = (now.getDay() + 6) % 7;
  // Set to nearest Thursday (current date + 4 - current day number)
  target.setDate(target.getDate() - dayNum + 3);
  // Get first Thursday of the year
  const firstThursday = new Date(target.getFullYear(), 0, 1);
  if (firstThursday.getDay() !== 4) {
    firstThursday.setMonth(0, 1 + ((4 - firstThursday.getDay() + 7) % 7));
  }
  // Calculate week number: 1 + number of weeks between first Thursday and current Thursday
  const weekNum = 1 + Math.round((target - firstThursday) / (7 * 24 * 60 * 60 * 1000));
  return weekNum;
};

/**
 * Get current ISO year (ISO 8601 compliant)
 * The ISO year is the year of the Thursday of the current week
 */
export const getCurrentISOYear = () => {
  const now = new Date();
  const target = new Date(now.valueOf());
  // ISO week starts on Monday, so adjust day number (Mon=1, Sun=7)
  const dayNum = (now.getDay() + 6) % 7;
  // Set to nearest Thursday to determine the ISO year
  target.setDate(target.getDate() - dayNum + 3);
  return target.getFullYear();
};

/**
 * Number of ISO weeks in a given year (52 or 53).
 * Dec 28 always falls in the last ISO week of its year.
 */
export const getISOWeeksInYear = (year) => {
  const dec28 = new Date(year, 11, 28);
  const dayNum = (dec28.getDay() + 6) % 7;
  const target = new Date(dec28);
  target.setDate(dec28.getDate() - dayNum + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 1);
  if (firstThursday.getDay() !== 4) {
    firstThursday.setMonth(0, 1 + ((4 - firstThursday.getDay() + 7) % 7));
  }
  return 1 + Math.round((target - firstThursday) / (7 * 24 * 60 * 60 * 1000));
};

/**
 * Get the Monday–Sunday date range for a given ISO week number and year.
 * @param {number} weekNumber - ISO week (1–53)
 * @param {number} year - Calendar year
 * @returns {{ start: Date, end: Date } | null}
 */
export const getISOWeekDateRange = (weekNumber, year) => {
  if (!weekNumber || !year || weekNumber < 1 || weekNumber > 53) return null;
  // Jan 4 is always in ISO week 1
  const jan4 = new Date(year, 0, 4);
  const dow = jan4.getDay(); // 0=Sun..6=Sat
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setDate(jan4.getDate() - (dow === 0 ? 6 : dow - 1));
  const start = new Date(mondayOfWeek1);
  start.setDate(mondayOfWeek1.getDate() + (weekNumber - 1) * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
};

// Shared tooltip title for weekly charts: "Week N · MM/DD/YYYY - MM/DD/YYYY"
export const formatWeekTooltipLabel = (weekNum, year) => {
  const range = getISOWeekDateRange(weekNum, year);
  if (!range) return `Week ${weekNum}`;
  const fmt = (d) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  return `Week ${weekNum} · ${fmt(range.start)} - ${fmt(range.end)}`;
};

/**
 * Format date range for display
 */
export const formatDateRange = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startStr = startDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
  const endStr = endDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return `${startStr} - ${endStr}`;
};

// =============================================================================
// FOB Price Dashboard Data Transformations
// =============================================================================

/**
 * Prepare FOB price average data for year-over-year line chart
 * Data comes as raw records: year, month, iso_week, district, avg_low_price, avg_high_price, avg_price, observation_count
 * Charts handle aggregation themselves
 */
export const prepareFobPriceAvgData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  // Pass through raw data - charts aggregate themselves
  return data;
};

/** API key order for `filter_options` on FOB average + by-origin endpoints (matches Price Pulse cascade). */
export const FOB_FILTER_OPTION_KEYS = [
  'type',
  'variety',
  'item_size',
  'size_group',
  'size_granularity',
  'size_extra_info',
  'grade',
  'package',
  'storage',
  'condition',
  'environment',
  'quality',
  'appearance',
  'properties',
];

/**
 * Normalize `filter_options` from FOB endpoints (snake_case keys, always-present lists).
 * @param {Record<string, string[]>|null|undefined} raw
 * @returns {Record<string, string[]>|null}
 */
export const normalizeFobFilterOptions = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  for (const key of FOB_FILTER_OPTION_KEYS) {
    const arr = raw[key];
    out[key] = Array.isArray(arr) ? arr : [];
  }
  return out;
};

/**
 * Prefer backend ordering from filter_options; intersect with values present in the current cascade slice.
 * @param {Record<string, string[]>|null|undefined} filterOptions
 * @param {string} key — e.g. 'type', 'item_size'
 * @param {string[]} dataOptions — distinct values from rows after upstream filters (any order)
 * @returns {string[]}
 */
export const fobCascadeOptions = (filterOptions, key, dataOptions) => {
  const list = Array.isArray(dataOptions) ? dataOptions : [];
  const fo = filterOptions?.[key];
  if (!Array.isArray(fo) || fo.length === 0) return list;
  const set = new Set(list);
  const ordered = fo.filter((x) => set.has(x));
  // Cucumber Size is size_granularity; keep data labels the served list omitted.
  if (key === 'size_granularity' && ordered.length > 0) {
    const seen = new Set(ordered);
    return [...ordered, ...list.filter((x) => !seen.has(x))];
  }
  return ordered.length > 0 ? ordered : list;
};

/**
 * Row property behind each single-select FOB characteristic filter, as
 * [resultKey, rowProperty]. Order is the cascade order the filter UI presents.
 */
export const FOB_COMBINATION_FIELDS = [
  ['type', 'type'],
  ['variety', 'variety'],
  ['size', 'item_size'],
  ['grade', 'grade'],
  ['package', 'package'],
  ['storage', 'storage'],
  ['condition', 'condition'],
  ['environment', 'environment'],
  ['quality', 'quality'],
  ['appearance', 'appearance'],
  ['properties', 'properties'],
];

/**
 * Row property behind each single-select characteristic filter on the daily
 * terminal price charts (Price Pulse, Terminal Price Pulse), as
 * [resultKey, rowProperty]. Order is the cascade order the filter UI presents.
 *
 * `package` reads `package` and falls back to `sku`, which is how both charts
 * match and label that filter.
 *
 * `type` reads an untyped row as Conventional. These charts filter type with
 * `!item.type || item.type === selected`, so a blank-type row is drawn under
 * whatever type is selected; folding blanks into Conventional keeps the score
 * in step with what the chart actually plots for the default selection.
 */
export const TERMINAL_COMBINATION_FIELDS = [
  ['type', (row) => row?.type || 'Conventional'],
  ['variety', 'variety'],
  ['size', 'item_size'],
  ['grade', 'grade'],
  ['package', (row) => row?.package ?? row?.sku],
  ['storage', 'storage'],
  ['condition', 'condition'],
  ['environment', 'environment'],
  ['quality', 'quality'],
  ['appearance', 'appearance'],
  ['properties', 'properties'],
];

/**
 * Size row reader (cucumbers): `size_granularity` when the package/variety
 * rule has a number (Field cartons 24s/36s). Field bushel has no Size option.
 * Other commodities keep raw `item_size`. Crop size rules live in pulse/, not
 * PLACEHOLDER_FREE_COMMODITIES (sentinel hiding).
 */
export const readSizeGranularity = (row) => {
  const rules = commoditySizeRules('cucumbers');
  return rules ? rules.readSize(row) : undefined;
};

/** Size Group row reader (`size_group`; NULL rows contribute no option). */
export const readSizeGroup = (row) => {
  const rules = commoditySizeRules('cucumbers');
  if (rules) return rules.readSizeGroup(row);
  return row?.size_group;
};

const readItemSize = (row) => row?.item_size;

/** The Size filter's row reader: crop size rules, otherwise `item_size`. */
export const sizeReader = (commodity) => {
  const rules = commoditySizeRules(commodity);
  if (rules?.readSize) return rules.readSize;
  return readItemSize;
};

/** Display-only INITCAP ("medium/select" -> "Medium/Select", "150-200s" stays). Never use before matching. */
export const initCap = (value) => {
  if (value == null) return value;
  return String(value).toLowerCase().replace(/(^|[^a-z0-9])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());
};

/** Tail of the characteristic chain, identical for every commodity. */
export const CHARACTERISTIC_TAIL = ['storage', 'condition', 'environment', 'quality', 'appearance', 'properties'];

/**
 * Characteristic chain below Type, as the filter UI cascades it.
 * Crop with size rules (cucumbers): Variety, Size Group, Package, Size, Grade, ...
 * Everything else: Variety, Size, Grade, Package, ... (unchanged).
 */
export const characteristicKeys = (commodity) =>
  commoditySizeRules(commodity)
    ? ['variety', 'sizeGroup', 'package', 'size', 'grade', ...CHARACTERISTIC_TAIL]
    : ['variety', 'size', 'grade', 'package', ...CHARACTERISTIC_TAIL];

/** Row property (or accessor) a chain key filters on. Package is the plain `package` field. */
export const characteristicReader = (key, commodity) => {
  if (key === 'size') return sizeReader(commodity);
  if (key === 'sizeGroup') return commoditySizeRules(commodity)?.readSizeGroup || readSizeGroup;
  return key;
};

/** Value of a row for a reader from {@link characteristicReader} (property name or accessor). */
export const readRowField = (row, reader) => (typeof reader === 'function' ? reader(row) : row?.[reader]);

/** `filter_options` key behind a chain key (FOB endpoints). */
export const characteristicFilterOptionKey = (key, commodity) => {
  if (key === 'size') return commoditySizeRules(commodity) ? 'size_granularity' : 'item_size';
  if (key === 'sizeGroup') return 'size_group';
  return key;
};

/**
 * Walk the chain: each key's option list sees the rows narrowed by the
 * selections above it. `listFor(key, rows)` builds a key's options and
 * `keepFor(key, row, value)` narrows by a selected value, so each chart keeps
 * its own list/match rules (FOB filter_options ordering, terminal sku fallback).
 * Returns the lists per key and the rows left after every selection.
 */
export const walkCharacteristicOptions = (rows, keys, selected, listFor, keepFor) => {
  let filtered = rows;
  const options = {};
  for (const key of keys) {
    options[key] = listFor(key, filtered);
    if (selected[key]) filtered = filtered.filter((row) => keepFor(key, row, selected[key]));
  }
  return { options, filtered };
};

/** FOB combination fields, in cascade order for `commodity`. Crops without size rules get {@link FOB_COMBINATION_FIELDS} itself. */
export const fobCombinationFields = (commodity) => {
  const sizeRules = commoditySizeRules(commodity);
  if (!sizeRules) return FOB_COMBINATION_FIELDS;
  return [
    ['type', 'type'],
    ['variety', 'variety'],
    ['sizeGroup', sizeRules.readSizeGroup || readSizeGroup],
    ['package', 'package'],
    ['size', sizeRules.readSize || readSizeGranularity],
    ['grade', 'grade'],
    ['storage', 'storage'],
    ['condition', 'condition'],
    ['environment', 'environment'],
    ['quality', 'quality'],
    ['appearance', 'appearance'],
    ['properties', 'properties'],
  ];
};

/** Terminal combination fields, in cascade order for `commodity`. Crops without size rules get {@link TERMINAL_COMBINATION_FIELDS} itself. */
export const terminalCombinationFields = (commodity) => {
  const sizeRules = commoditySizeRules(commodity);
  if (!sizeRules) return TERMINAL_COMBINATION_FIELDS;
  return [
    TERMINAL_COMBINATION_FIELDS[0],
    ['variety', 'variety'],
    ['sizeGroup', sizeRules.readSizeGroup || readSizeGroup],
    TERMINAL_COMBINATION_FIELDS[4],
    ['size', sizeRules.readSize || readSizeGranularity],
    ['grade', 'grade'],
    ...TERMINAL_COMBINATION_FIELDS.slice(5),
  ];
};

/**
 * Values to favour when two combinations are otherwise equally well covered.
 * Type is scored on data like every other field — this only breaks exact ties,
 * so Conventional wins a coin flip but never beats a better-covered type.
 */
export const DEFAULT_COMBINATION_PREFERENCE = { type: 'Conventional' };

/** Pre-change spellings the auto-pick treats as unset (case-sensitive, kept for commodities not in the list). */
const LEGACY_UNSET_COMBINATION_VALUES = new Set([
  'N/A', 'n/a', 'NA', 'na', 'Not Classified', 'null', 'NULL', '',
]);

const cleanCombinationValue = (value, commodity) => {
  const v = value == null ? '' : String(value).trim();
  const unset = hidesPlaceholders(commodity) ? isSentinelOption(v) : LEGACY_UNSET_COMBINATION_VALUES.has(v);
  return unset ? '' : v;
};

const readCombinationField = (row, accessor, commodity) =>
  cleanCombinationValue(typeof accessor === 'function' ? accessor(row) : row?.[accessor], commodity);

const emptyCombination = (fields) => Object.fromEntries(fields.map(([key]) => [key, '']));

/**
 * Fields decided on their own total coverage before the rest are scored as a
 * combination. Type is the one that matters: it is the top of every cascade and
 * the thing users read first, so "which type has more data" must beat "which
 * single SKU has more data". See `rankFirst` in {@link pickBestCombination}.
 */
export const DEFAULT_RANK_FIRST = ['type'];

/** The value of one field covering the most buckets across `rows`. */
const pickWidestCoverageValue = (rows, accessor, bucketOf, preferredValue, commodity) => {
  const coverage = new Map();
  for (const row of rows) {
    const bucket = typeof bucketOf === 'function' ? bucketOf(row) : null;
    if (bucket === null || bucket === undefined || bucket === '') continue;
    const value = readCombinationField(row, accessor, commodity);
    let seen = coverage.get(value);
    if (!seen) {
      seen = new Set();
      coverage.set(value, seen);
    }
    seen.add(String(bucket));
  }

  let best = null;
  for (const [value, seen] of coverage) {
    if (best === null || seen.size > best.size) {
      best = { value, size: seen.size };
      continue;
    }
    if (seen.size < best.size) continue;
    // Tie: the preferred value wins, otherwise stay deterministic.
    if (preferredValue && value === preferredValue) best = { value, size: seen.size };
    else if (preferredValue && best.value === preferredValue) continue;
    else if (value < best.value) best = { value, size: seen.size };
  }
  return best ? best.value : '';
};

/**
 * Pick the characteristic combination that puts the most points on the chart.
 *
 * Replaces picking the most common value one field at a time. That is greedy:
 * each field is optimised in isolation against the slice the previous field
 * left behind, so across eleven dependent fields it can settle on a cell with
 * plenty of rows but very few distinct periods — a short, gappy line, and an
 * empty year-over-year comparison when the cell only covers one year.
 *
 * Scoring is on distinct `bucketOf` values, which the caller defines to be
 * whatever the chart draws one point per (an ISO week, a calendar day), so
 * multi-period coverage is rewarded without needing a hand-tuned weight. Row
 * count breaks ties (denser sampling inside the same buckets), then `preferred`
 * (e.g. Conventional over Organic when both are equally covered), then the
 * tuple itself so the result is deterministic.
 *
 * Rows are grouped by their full attribute tuple with placeholders normalised
 * to `''` (= "don't filter on this field"), matching how the charts apply a
 * selection. That normalisation is what lets "leave this filter unset" win:
 * a field that is blank on most rows keeps those rows together in one large
 * group, instead of being skipped in favour of a rare concrete value that
 * discards them. Where a field normalises to `''` the group's real coverage can
 * be larger than scored, since an unset filter also admits rows carrying a
 * concrete value — an underestimate, never an overestimate.
 *
 * @param {object[]} rows — records already scoped to the chart's years/origins
 * @param {object}   options
 * @param {Array<[string, string|Function]>} options.fields — [resultKey, rowProperty|accessor]
 * @param {(row: object) => string|number|null} options.bucketOf — the row's x-axis
 *   bucket, or a falsy value when the row draws no point (no price, no date)
 * @param {Record<string, string>} [options.preferred] — per-key value to favour on ties
 * @param {string[]} [options.rankFirst] — fields decided on their own total coverage
 *   before the rest are scored together; see {@link DEFAULT_RANK_FIRST}
 * @param {string} [options.commodity] — the chart's commodity; placeholders are "unset" per {@link hidesPlaceholders}
 * @returns {Record<string, string>} one value per `fields` key
 */
export const pickBestCombination = (rows, options = {}) => {
  const { fields = [], bucketOf, preferred = null, rankFirst = [], commodity = undefined } = options;
  if (!Array.isArray(rows) || rows.length === 0 || fields.length === 0) {
    return emptyCombination(fields);
  }

  // Decide the ranked fields on their own coverage first, then score the rest
  // within that slice. Scoring type as just another part of the tuple lets a
  // type whose rows are spread thinly across many SKUs lose to a type holding
  // far less data but one concentrated SKU — the chart would open on Organic
  // while Conventional carried three times the days.
  let scoped = rows;
  const ranked = {};
  for (const key of rankFirst) {
    const entry = fields.find(([k]) => k === key);
    if (!entry) continue;
    const value = pickWidestCoverageValue(scoped, entry[1], bucketOf, preferred?.[key], commodity);
    ranked[key] = value;
    if (value) {
      scoped = scoped.filter((row) => readCombinationField(row, entry[1], commodity) === value);
    }
  }

  const groups = new Map();
  for (const row of scoped) {
    const bucket = typeof bucketOf === 'function' ? bucketOf(row) : null;
    // Same test the chart uses to decide a row contributes a point.
    if (bucket === null || bucket === undefined || bucket === '') continue;

    const values = fields.map(([, accessor]) => readCombinationField(row, accessor, commodity));
    const key = values.join('\u0000');
    let group = groups.get(key);
    if (!group) {
      group = { values, buckets: new Set(), rowCount: 0 };
      groups.set(key, group);
    }
    group.buckets.add(String(bucket));
    group.rowCount += 1;
  }

  const preferredScore = (values) => {
    if (!preferred) return 0;
    let matches = 0;
    fields.forEach(([key], i) => {
      if (preferred[key] && values[i] === preferred[key]) matches += 1;
    });
    return matches;
  };

  let best = null;
  let bestKey = '';
  let bestPreferred = -1;
  for (const [key, group] of groups) {
    const pref = preferredScore(group.values);
    const better =
      best === null ||
      group.buckets.size > best.buckets.size ||
      (group.buckets.size === best.buckets.size &&
        (group.rowCount > best.rowCount ||
          (group.rowCount === best.rowCount &&
            (pref > bestPreferred || (pref === bestPreferred && key < bestKey)))));
    if (better) {
      best = group;
      bestKey = key;
      bestPreferred = pref;
    }
  }

  if (!best) return { ...emptyCombination(fields), ...ranked };
  const result = Object.fromEntries(fields.map(([key], i) => [key, best.values[i]]));
  // A ranked field stays whatever its own coverage chose, including ''.
  return { ...result, ...ranked };
};

/**
 * {@link pickBestCombination} for the FOB charts, which draw one point per
 * `(year, iso_week)` or `(year, month)` depending on granularity.
 *
 * @param {object[]} rows — raw FOB records already scoped to years/origins
 * @param {object}   [options]
 * @param {'weekly'|'monthly'} [options.granularity='weekly'] — must match the chart's
 * @param {'low'|'avg'|'high'} [options.priceType='avg'] — must match the chart's
 * @param {Record<string, string>} [options.preferred] — per-key value to favour on ties
 * @returns {Record<string, string>} one value per {@link FOB_COMBINATION_FIELDS} key
 */
export const pickBestFobCombination = (rows, options = {}) => {
  const {
    granularity = 'weekly',
    priceType = 'avg',
    preferred = DEFAULT_COMBINATION_PREFERENCE,
    rankFirst = DEFAULT_RANK_FIRST,
    commodity = undefined,
  } = options;

  const priceField =
    priceType === 'low' ? 'avg_low_price' : priceType === 'high' ? 'avg_high_price' : 'avg_price';

  return pickBestCombination(rows, {
    fields: fobCombinationFields(commodity),
    preferred,
    rankFirst,
    commodity,
    bucketOf: (row) => {
      const period = granularity === 'weekly' ? row.iso_week : row.month;
      if (!row[priceField] || !period) return null;
      return `${row.year}-${period}`;
    },
  });
};

/**
 * {@link pickBestCombination} for the daily terminal price charts, which draw
 * one point per calendar day (`date_string`) on each district/terminal line.
 *
 * A row counts only when it carries a numeric price in one of `priceTypes`,
 * mirroring the charts' own `typeof priceValue === 'number'` guard.
 *
 * @param {object[]} rows — raw terminal records already scoped to origins/locations
 * @param {object}   [options]
 * @param {string[]} [options.priceTypes=['low_price','high_price']] — must match the chart's
 * @param {Record<string, string>} [options.preferred] — per-key value to favour on ties
 * @returns {Record<string, string>} one value per {@link TERMINAL_COMBINATION_FIELDS} key
 */
export const pickBestTerminalCombination = (rows, options = {}) => {
  const {
    priceTypes = ['low_price', 'high_price'],
    preferred = DEFAULT_COMBINATION_PREFERENCE,
    rankFirst = DEFAULT_RANK_FIRST,
    commodity = undefined,
  } = options;

  return pickBestCombination(rows, {
    fields: terminalCombinationFields(commodity),
    preferred,
    rankFirst,
    commodity,
    bucketOf: (row) => {
      const hasPrice = priceTypes.some((field) => {
        const v = row?.[field];
        return typeof v === 'number' && !Number.isNaN(v);
      });
      if (!hasPrice) return null;
      return row?.date_string || null;
    },
  });
};

/**
 * Prepare FOB price by origin data for heatmap and line charts
 * Data comes as raw records: year, month, iso_week, origin, avg_low_price, avg_high_price, avg_price, observation_count
 * Charts handle aggregation themselves
 */
export const prepareFobPriceByOriginData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  // Pass through raw data - charts aggregate themselves
  return data;
};

/**
 * Prepare seasonality data
 * Data comes as raw volume records: date, year, month, iso_week, origin, volume
 * Charts handle aggregation themselves
 */
export const prepareFobSeasonalityData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  // Pass through raw data - charts aggregate themselves
  return data;
};

// =============================================================================
// Market Share Dashboard Data Transformations
// =============================================================================

/**
 * Prepare market share chart data for stacked area chart
 * Data comes as raw volume records: date, year, month, iso_week, origin, volume
 * Charts handle aggregation and market share calculation themselves
 */
export const prepareMarketShareChartData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  // Pass through raw data - charts aggregate themselves
  return data;
};

/**
 * Prepare market share summary data for table display
 * Data comes as raw volume records: date, year, month, iso_week, origin, volume
 * Charts handle aggregation and market share calculation themselves
 */
export const prepareMarketShareSummaryData = (data) => {
  if (!data || !Array.isArray(data)) return [];
  // Pass through raw data - charts aggregate themselves
  return data;
};

/**
 * Get trend indicator color
 */
export const getTrendColor = (trend) => {
  switch (trend) {
    case 'strong_growth':
      return '#22c55e'; // Green
    case 'growing':
      return '#86efac'; // Light green
    case 'stable':
      return '#fbbf24'; // Yellow
    case 'slight_decline':
      return '#fb923c'; // Orange
    case 'declining':
      return '#ef4444'; // Red
    default:
      return '#9ca3af'; // Gray
  }
};

/**
 * Get trend indicator label
 */
export const getTrendLabel = (trend) => {
  switch (trend) {
    case 'strong_growth':
      return 'Strong Growth';
    case 'growing':
      return 'Growing';
    case 'stable':
      return 'Stable';
    case 'slight_decline':
      return 'Slight Decline';
    case 'declining':
      return 'Declining';
    default:
      return 'Unknown';
  }
};

/**
 * Calculate a smart Y-axis domain that avoids wasting space when values are in a narrow high range.
 * - Adds ~10% padding above and below
 * - If min is close to 0 (within 20% of range), starts from 0
 * - Returns [paddedMin, paddedMax] for use as Recharts YAxis domain
 */
export const getSmartYDomain = (chartData, dataKeys) => {
  let min = Infinity;
  let max = -Infinity;

  for (const item of chartData) {
    for (const key of dataKeys) {
      const val = item[key];
      if (typeof val === 'number' && !isNaN(val)) {
        if (val < min) min = val;
        if (val > max) max = val;
      }
    }
  }

  if (min === Infinity || max === -Infinity) return [0, 'auto'];

  const range = max - min;
  const padding = range * 0.1 || max * 0.1 || 1;

  // If min is close to 0 (within 20% of range), start from 0
  const paddedMin = min <= range * 0.2 ? 0 : Math.floor((min - padding) * 100) / 100;
  const paddedMax = Math.ceil((max + padding) * 100) / 100;

  return [Math.max(0, paddedMin), paddedMax];
};

/**
 * Parse a display date string like "6 Jun 2025" back to a timestamp for sorting
 */
export const parseDateDisplay = (str) => {
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };
  const parts = str.split(' ');
  if (parts.length !== 3) return 0;
  const day = parseInt(parts[0], 10);
  const month = months[parts[1]];
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || month === undefined || isNaN(year)) return 0;
  return new Date(year, month, day).getTime();
};

/**
 * Get delta color (for price comparisons - lower is green, higher is red)
 */
export const getDeltaColor = (delta, lowerIsGood = true) => {
  if (delta === null || delta === undefined) return '#9ca3af';
  if (lowerIsGood) {
    return delta < 0 ? '#22c55e' : delta > 0 ? '#ef4444' : '#9ca3af';
  }
  return delta > 0 ? '#22c55e' : delta < 0 ? '#ef4444' : '#9ca3af';
};

// =============================================================================
// Cargo Varieties (Peru) Helpers
// =============================================================================

/**
 * Prepare cargo varieties period data into Recharts-ready rows for the butterfly chart.
 * East coast values are stored as negatives so bars extend left; labels show absolute values.
 *
 * @param {Object} period - One period object from the API (e.g. periods["7d"])
 * @param {string} metric - "boxes" | "weight_kg"
 * @returns {{ rows: Array, maxValue: number }}
 */
export const prepareCargoVarietiesData = (period, metric = 'boxes') => {
  if (!period || !period.varieties) return { rows: [], maxValue: 0 };

  const rows = period.varieties.map((v) => ({
    variety: v.variety,
    type_of_variety: v.type_of_variety || '',
    east: -(v.east_coast?.[metric] || 0),
    west: v.west_coast?.[metric] || 0,
    eastAbs: v.east_coast?.[metric] || 0,
    westAbs: v.west_coast?.[metric] || 0,
    total: v[metric] || 0,
    eastPorts: v.east_coast?.ports || {},
    westPorts: v.west_coast?.ports || {},
  }));

  const maxValue = rows.reduce((acc, r) => Math.max(acc, r.eastAbs, r.westAbs), 0);

  return { rows, maxValue };
};

